import React from 'react';
import { API_BASE_URL } from '../services/api';
import hardwareService, { HardwareDevice } from '../services/hardwareService';
import {
  appendCameraStreamAuthIfNeeded,
  buildHardwareHlsUrls,
  defaultFourMagazynUrls,
  padCameraUrlsToFour,
  shouldUseHardwareHlsProxy,
} from '../utils/projectCameraStreams';

/**
 * Gdy panel kamer jest otwarty i podano project_id — pobiera /api/hardware?project_id=…
 * i ustawia URL-e HLS z adresu urządzenia (Tailscale / IP). W przeciwnym razie — stare domyślne magazynowe.
 */
export function useProjectCameraHlsUrls(panelOpen: boolean, projectId: string | null | undefined): {
  urls: string[];
  loading: boolean;
  hasHardwareCameras: boolean;
  hardwareDevices: HardwareDevice[];
} {
  const [devices, setDevices] = React.useState<HardwareDevice[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!panelOpen || !projectId) {
      setDevices([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    hardwareService
      .list(projectId)
      .then((list) => {
        if (!cancelled) setDevices(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setDevices([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [panelOpen, projectId]);

  const hasHardwareCameras = React.useMemo(
    () => devices.some((d) => (Number(d.camera_count) || 0) > 0),
    [devices]
  );

  const urls = React.useMemo(() => {
    if (!panelOpen || !projectId) return defaultFourMagazynUrls();
    // Dopóki trwa GET /hardware — nie podstawiaj fallbacku magazynu (może być pusty / z env), bo hls.js
    // zaczyna ładować zły host i zostaje czarny ekran nawet po przyjściu właściwych URL-i z API.
    if (loading) return padCameraUrlsToFour([]);
    const withCam = devices.filter((d) => (Number(d.camera_count) || 0) > 0);
    const useProxy = shouldUseHardwareHlsProxy();
    const built = buildHardwareHlsUrls(withCam, 4, {
      useProxy,
      apiBase: API_BASE_URL,
    });
    if (built.length > 0) {
      return padCameraUrlsToFour(built.map(appendCameraStreamAuthIfNeeded));
    }
    return defaultFourMagazynUrls();
  }, [panelOpen, projectId, devices, loading]);

  return { urls, loading, hasHardwareCameras, hardwareDevices: devices };
}
